import { Extension } from "@tiptap/core";
import Color from "@tiptap/extension-color";
import Emoji, { gitHubEmojis } from "@tiptap/extension-emoji";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { Mention } from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import type { SuggestionOptions } from "@tiptap/suggestion";
import tippy, { type Instance as TippyInstance } from "tippy.js";

export const IMAGE_EMBEDDING_MAX_WIDTH = 500;

// Rewrite Giphy and Tenor page URLs to direct media URLs.
export const convertToDirectImageUrl = (url: string): string => {
	try {
		const urlObj = new URL(url);

		if (
			urlObj.hostname.includes("giphy.com") &&
			urlObj.pathname.includes("/gifs/")
		) {
			const pathAfterGifs = urlObj.pathname.split("/gifs/")[1];
			const gifId = pathAfterGifs.includes("-")
				? pathAfterGifs.split("-").pop()
				: pathAfterGifs;
			return `https://media.giphy.com/media/${gifId}/giphy.gif`;
		}

		if (
			urlObj.hostname.includes("tenor.com") &&
			urlObj.pathname.includes("/view/")
		) {
			const pathAfterView = urlObj.pathname.split("/view/")[1];
			const tenorId = pathAfterView.includes("-")
				? pathAfterView.split("-").pop()
				: pathAfterView;
			return `https://media.tenor.com/images/${tenorId}/tenor.gif`;
		}

		return url;
	} catch {
		return url;
	}
};

const isImageUrl = async (url: string): Promise<boolean> => {
	try {
		new URL(url);

		const response = await fetch(url, {
			method: "HEAD",
			mode: "cors",
			cache: "no-cache",
		});

		if (!response.ok) return false;

		const contentType = response.headers.get("content-type");

		return contentType ? contentType.startsWith("image/") : false;
	} catch {
		return false;
	}
};

// Override Shift+Enter to split block (new paragraph) instead of inserting <br>
const HardLineBreak = Extension.create({
	name: "hardLineBreak",

	addKeyboardShortcuts() {
		return {
			"Shift-Enter": ({ editor }) => editor.commands.splitBlock(),
		};
	},
});

const AutoImagePaste = Extension.create({
	name: "autoImagePaste",

	addProseMirrorPlugins() {
		return [
			new Plugin({
				key: new PluginKey("autoImagePaste"),
				props: {
					handlePaste(view, event) {
						const text = event.clipboardData?.getData("text/plain");

						if (text) {
							try {
								new URL(text);

								const { schema, tr } = view.state;
								const textNode = schema.text(text);
								const transaction = tr.replaceSelectionWith(textNode, false);
								const insertPos = transaction.selection.from - text.length;
								view.dispatch(transaction);

								isImageUrl(text).then((isImage) => {
									if (isImage) {
										const directUrl = convertToDirectImageUrl(text);
										const imageNode = schema.nodes.image.create({
											src: directUrl,
											width: IMAGE_EMBEDDING_MAX_WIDTH.toString(),
										});
										const replaceTr = view.state.tr.replaceWith(
											insertPos,
											insertPos + text.length,
											imageNode
										);
										view.dispatch(replaceTr);
									}
								});
								return true;
							} catch {
								return false;
							}
						}

						return false;
					},
					transformPastedHTML(html) {
						const parser = new DOMParser();
						const doc = parser.parseFromString(html, "text/html");
						const images = doc.querySelectorAll("img");

						// biome-ignore lint/complexity/noForEach: <explanation>
						images.forEach((img) => {
							img.setAttribute("width", IMAGE_EMBEDDING_MAX_WIDTH.toString());
							// Remove height to maintain aspect ratio
							img.removeAttribute("height");
						});

						return doc.body.innerHTML;
					},
				},
			}),
		];
	},
});

const ImageResize = Extension.create({
	name: "imageResize",

	addProseMirrorPlugins() {
		return [
			new Plugin({
				key: new PluginKey("imageResize"),
				props: {
					handleDOMEvents: {
						mousemove(view, event) {
							const target = event.target as HTMLElement;
							const img = target.closest("img");

							if (!img) return false;

							const pos = view.posAtDOM(img, 0);
							if (pos === null || pos === undefined) return false;

							const $pos = view.state.doc.resolve(pos);
							const node = $pos.nodeAfter;

							if (!node || node.type.name !== "image") return false;

							const rect = img.getBoundingClientRect();
							const mouseX = event.clientX - rect.left;

							if (mouseX > rect.width - 20) {
								img.style.cursor = "ew-resize";
							} else {
								img.style.cursor = "default";
							}

							return false;
						},
						mousedown(view, event) {
							const target = event.target as HTMLElement;
							const img = target.closest("img");

							if (!img) return false;

							const pos = view.posAtDOM(img, 0);
							if (pos === null || pos === undefined) return false;

							const $pos = view.state.doc.resolve(pos);
							const node = $pos.nodeAfter;

							if (!node || node.type.name !== "image") return false;

							const startX = event.clientX;
							const startWidth = img.width || img.naturalWidth;
							const startHeight = img.height || img.naturalHeight;
							const aspectRatio = startWidth / startHeight;

							const onMouseMove = (e: MouseEvent) => {
								const deltaX = e.clientX - startX;
								const newWidth = Math.min(
									IMAGE_EMBEDDING_MAX_WIDTH,
									Math.max(50, startWidth + deltaX)
								);
								const newHeight = newWidth / aspectRatio;

								const tr = view.state.tr.setNodeMarkup(pos, undefined, {
									...node.attrs,
									width: `${Math.round(newWidth)}`,
									height: `${Math.round(newHeight)}`,
								});
								view.dispatch(tr);
							};

							const onMouseUp = () => {
								document.removeEventListener("mousemove", onMouseMove);
								document.removeEventListener("mouseup", onMouseUp);
								img.style.cursor = "default";
							};

							const rect = img.getBoundingClientRect();
							const clickX = event.clientX - rect.left;

							if (clickX > rect.width - 20) {
								document.addEventListener("mousemove", onMouseMove);
								document.addEventListener("mouseup", onMouseUp);
								event.preventDefault();
								return true;
							}

							return false;
						},
					},
				},
			}),
		];
	},
});

export type MentionUser = {
	type: "user";
	id: string;
	firstName?: string;
	lastName?: string;
	preferredName?: string | null;
};

export type MentionRole = {
	type: "role";
	id: string;
	roleType: "workspace";
	role: string;
	workspaceId?: string;
};

export type MentionItem = MentionUser | MentionRole;

const formatRoleName = (role: string): string =>
	role
		.split("-")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");

const getUserDisplayName = (user: MentionUser): string => {
	if (user.preferredName) {
		return user.preferredName;
	}
	if (user.firstName && user.lastName) {
		return `${user.firstName} ${user.lastName}`;
	}
	if (user.firstName) {
		return user.firstName;
	}
	return "";
};

const getDisplayName = (item: MentionItem): string => {
	if (item.type === "role") {
		return formatRoleName(item.role);
	}
	return getUserDisplayName(item);
};

const getUserInitials = (user: MentionUser): string => {
	const first = user.firstName?.[0] || "";
	const last = user.lastName?.[0] || "";
	return (first + last).toUpperCase() || "U";
};

export type MentionItemsGetter = () => {
	users: Omit<MentionUser, "type">[];
	roles: Omit<MentionRole, "type">[];
};

const CustomMention = Mention.extend({
	addAttributes() {
		return {
			...this.parent?.(),
			roleType: {
				default: null,
				parseHTML: (element) => element.getAttribute("data-role-type"),
				renderHTML: (attributes) => {
					if (!attributes.roleType) return {};
					return { "data-role-type": attributes.roleType };
				},
			},
			role: {
				default: null,
				parseHTML: (element) => element.getAttribute("data-role"),
				renderHTML: (attributes) => {
					if (!attributes.role) return {};
					return { "data-role": attributes.role };
				},
			},
			workspaceId: {
				default: null,
				parseHTML: (element) => element.getAttribute("data-workspace-id"),
				renderHTML: (attributes) => {
					if (!attributes.workspaceId) return {};
					return { "data-workspace-id": attributes.workspaceId };
				},
			},
		};
	},
});

export const getMentionExtension = (getItems: MentionItemsGetter) =>
	CustomMention.configure({
		HTMLAttributes: {
			class: "mention bg-primary/10 text-primary px-1 rounded font-medium",
		},
		renderLabel({ node }) {
			return `@${node.attrs.label || ""}`;
		},
		suggestion: {
			command: ({ editor, range, props }) => {
				const nodeAttrs: Record<string, unknown> = {
					id: props.id,
					label: props.label,
				};

				if (props.type === "role") {
					nodeAttrs.roleType = props.roleType;
					nodeAttrs.role = props.role;
					if (props.workspaceId) {
						nodeAttrs.workspaceId = props.workspaceId;
					}
				}

				editor
					.chain()
					.focus()
					.insertContentAt(range, [
						{ type: "mention", attrs: nodeAttrs },
						{ type: "text", text: " " },
					])
					.run();
			},
			items: ({ query }) => {
				const { users, roles } = getItems();
				const searchQuery = query.toLowerCase();

				const filteredUsers = users
					.filter((user) => {
						if (!searchQuery) return true;
						const fullName =
							`${user.firstName || ""} ${user.lastName || ""}`.toLowerCase();
						const preferredName = user.preferredName?.toLowerCase() || "";

						return (
							fullName.includes(searchQuery) ||
							preferredName.includes(searchQuery) ||
							user.firstName?.toLowerCase().includes(searchQuery) ||
							user.lastName?.toLowerCase().includes(searchQuery)
						);
					})
					.map((u) => ({ ...u, type: "user" as const }));

				const filteredRoles = roles
					.filter(
						(role) =>
							!searchQuery || role.role.toLowerCase().includes(searchQuery)
					)
					.map((r) => ({ ...r, type: "role" as const }));

				const hasUsers = filteredUsers.length > 0;
				const hasRoles = filteredRoles.length > 0;
				const sliceLimit = hasUsers && hasRoles ? 5 : 10;

				const slicedUsers = filteredUsers.slice(0, sliceLimit);
				const slicedRoles = filteredRoles.slice(0, sliceLimit);

				return [...slicedUsers, ...slicedRoles].map((item) => ({
					...item,
					label: getDisplayName(item),
				}));
			},
			render: () => {
				let component: HTMLElement;
				let popup: TippyInstance[];
				let selectedIndex = 0;
				let currentItems: MentionItem[] = [];
				let currentCommand: ((item: MentionItem) => void) | null = null;

				const renderItems = (props: {
					items: MentionItem[];
					command: (item: MentionItem) => void;
				}) => {
					currentItems = props.items;
					currentCommand = props.command;
					component.innerHTML = "";

					if (props.items.length === 0) {
						const noResults = document.createElement("div");
						noResults.className = "px-2 py-1.5 text-sm text-muted-foreground";
						noResults.textContent = "No results found";
						component.appendChild(noResults);
						return;
					}

					const users = currentItems.filter(
						(item) => item.type === "user"
					) as MentionUser[];
					const roles = currentItems.filter(
						(item) => item.type === "role"
					) as MentionRole[];

					for (const [index, user] of users.entries()) {
						const button = document.createElement("button");
						const isSelected = index === selectedIndex;
						button.className = `mention-suggestion-item flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-accent cursor-pointer ${
							isSelected ? "bg-accent" : ""
						}`;

						const displayName = getUserDisplayName(user);
						const initials = getUserInitials(user);
						const initialsSpan = document.createElement("span");
						initialsSpan.className = "inline-flex items-center justify-center w-6 h-6 rounded-full bg-muted text-muted-foreground text-xs font-medium";
						initialsSpan.textContent = initials;
						const nameSpan = document.createElement("span");
						nameSpan.className = "font-medium text-foreground";
						nameSpan.textContent = displayName;
						button.appendChild(initialsSpan);
						button.appendChild(nameSpan);
						button.addEventListener("click", () => props.command(user));
						component.appendChild(button);
					}

					if (users.length > 0 && roles.length > 0) {
						const separator = document.createElement("div");
						separator.className = "h-px bg-border my-1";
						component.appendChild(separator);
					}

					for (const [index, role] of roles.entries()) {
						const button = document.createElement("button");
						const isSelected = users.length + index === selectedIndex;
						button.className = `mention-suggestion-item flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-accent cursor-pointer ${
							isSelected ? "bg-accent" : ""
						}`;

						const displayName = formatRoleName(role.role);
						const roleSpan = document.createElement("span");
						roleSpan.className = "text-primary font-medium";
						roleSpan.textContent = `@${displayName}`;
						button.appendChild(roleSpan);
						button.addEventListener("click", () => props.command(role));
						component.appendChild(button);
					}
				};

				return {
					onStart: (props) => {
						component = document.createElement("div");
						component.className =
							"mention-suggestion-list border rounded-md shadow-lg bg-popover p-1 max-h-96 overflow-y-auto";

						popup = tippy("body", {
							getReferenceClientRect: props.clientRect as () => DOMRect,
							appendTo: () => document.body,
							content: component,
							showOnCreate: true,
							interactive: true,
							trigger: "manual",
							placement: "bottom-start",
						});

						renderItems(
							props as {
								items: MentionItem[];
								command: (item: MentionItem) => void;
							}
						);
					},
					onUpdate(props) {
						selectedIndex = 0;
						renderItems(
							props as {
								items: MentionItem[];
								command: (item: MentionItem) => void;
							}
						);

						if (popup?.[0]) {
							popup[0].setProps({
								getReferenceClientRect: props.clientRect as () => DOMRect,
							});
						}
					},
					onKeyDown(props) {
						if (props.event.key === "Escape") {
							popup?.[0]?.hide();
							return true;
						}

						if (props.event.key === "ArrowUp") {
							props.event.preventDefault();
							selectedIndex =
								(selectedIndex - 1 + currentItems.length) % currentItems.length;
							const buttons = component.querySelectorAll(
								".mention-suggestion-item"
							);
							for (const [i, btn] of buttons.entries()) {
								btn.classList.toggle("bg-accent", i === selectedIndex);
							}
							return true;
						}

						if (props.event.key === "ArrowDown") {
							props.event.preventDefault();
							selectedIndex = (selectedIndex + 1) % currentItems.length;
							const buttons = component.querySelectorAll(
								".mention-suggestion-item"
							);
							for (const [i, btn] of buttons.entries()) {
								btn.classList.toggle("bg-accent", i === selectedIndex);
							}
							return true;
						}

						if (props.event.key === "Enter" || props.event.key === "Tab") {
							props.event.preventDefault();
							if (currentItems[selectedIndex] && currentCommand) {
								currentCommand(currentItems[selectedIndex]);
							}
							return true;
						}

						return false;
					},
					onExit() {
						popup?.[0]?.destroy();
						component?.remove();
					},
				};
			},
		} as Partial<SuggestionOptions>,
	});

export const getTiptapExtensions = (
	placeholderText = "",
	getMentionItems: MentionItemsGetter = () => ({ users: [], roles: [] })
) => [
	StarterKit.configure({
		hardBreak: false,
		bulletList: {
			keepMarks: true,
			keepAttributes: false,
		},
		orderedList: {
			keepMarks: true,
			keepAttributes: false,
		},
	}),
	Link.configure({
		openOnClick: false,
		autolink: true,
	}),
	Placeholder.configure({
		placeholder: placeholderText,
	}),
	Image.extend({
		inline: true,
		group: "inline",
		addAttributes() {
			return {
				...this.parent?.(),
				width: {
					default: null,
					parseHTML: (element: HTMLElement) => element.getAttribute("width"),
					renderHTML: (attributes: Record<string, any>) => {
						if (!attributes.width) {
							return {};
						}
						return { width: attributes.width };
					},
				},
				height: {
					default: null,
					parseHTML: (element: HTMLElement) => element.getAttribute("height"),
					renderHTML: (attributes: Record<string, any>) => {
						if (!attributes.height) {
							return {};
						}
						return { height: attributes.height };
					},
				},
			};
		},
		addNodeView() {
			return ({
				node,
				getPos,
				editor,
			}: {
				node: any;
				getPos: any;
				editor: any;
			}) => {
				const container = document.createElement("span");
				container.classList.add(
					"image-container",
					"relative",
					"group",
					"inline-block"
				);

				const img = document.createElement("img");
				img.src = node.attrs.src;
				img.alt = node.attrs.alt || "";
				img.classList.add(
					"rounded-lg",
					"max-w-full",
					"h-auto",
					"cursor-pointer"
				);

				if (node.attrs.width) {
					img.setAttribute("width", node.attrs.width);
				}
				if (node.attrs.height) {
					img.setAttribute("height", node.attrs.height);
				}

				const toggleBtn = document.createElement("button");
				toggleBtn.textContent = "🔗";
				toggleBtn.title = "Convert to editable link";
				toggleBtn.classList.add(
					"absolute",
					"top-2",
					"right-2",
					"opacity-0",
					"group-hover:opacity-100",
					"bg-black/60",
					"hover:bg-black/80",
					"text-white",
					"rounded",
					"px-2",
					"py-1",
					"text-xs",
					"transition-opacity",
					"z-10"
				);

				toggleBtn.onclick = (e) => {
					e.preventDefault();
					e.stopPropagation();

					if (typeof getPos === "function" && editor) {
						const pos = getPos();
						if (pos !== null && pos !== undefined) {
							const { schema } = editor.state;
							const textNode = schema.text(node.attrs.src, [
								schema.marks.link.create({ href: node.attrs.src }),
							]);
							const tr = editor.state.tr.replaceWith(
								pos,
								pos + node.nodeSize,
								textNode
							);
							editor.view.dispatch(tr);
							editor.commands.focus();
						}
					}
				};

				img.onerror = () => {
					if (typeof getPos === "function" && editor) {
						const pos = getPos();
						if (pos !== null && pos !== undefined) {
							const { schema } = editor.state;
							const textNode = schema.text(node.attrs.src, [
								schema.marks.link.create({ href: node.attrs.src }),
							]);
							const tr = editor.state.tr.replaceWith(
								pos,
								pos + node.nodeSize,
								textNode
							);
							editor.view.dispatch(tr);
						}
					}
				};

				container.appendChild(img);
				container.appendChild(toggleBtn);

				return {
					dom: container,
				};
			};
		},
	}).configure({
		inline: false,
		allowBase64: true,
	}),
	Emoji.configure({
		emojis: gitHubEmojis,
		enableEmoticons: true,
		suggestion: {
			items: ({ query }) =>
				gitHubEmojis
					.filter((emoji) => {
						const searchQuery = query.toLowerCase();
						return (
							emoji.name.toLowerCase().includes(searchQuery) ||
							emoji.shortcodes.some((shortcode) =>
								shortcode.toLowerCase().includes(searchQuery)
							) ||
							emoji.tags.some((tag) => tag.toLowerCase().includes(searchQuery))
						);
					})
					.slice(0, 10),
			render: () => {
				let component: HTMLElement;
				let popup: TippyInstance[];
				let selectedIndex = 0;
				let currentItems: any[] = [];
				let currentCommand: ((item: any) => void) | null = null;

				return {
					onStart: (props) => {
						component = document.createElement("div");
						component.className =
							"emoji-suggestion-list border rounded-md shadow-lg bg-popover p-1 max-h-60 overflow-y-auto";

						popup = tippy("body", {
							getReferenceClientRect: props.clientRect as () => DOMRect,
							appendTo: () => document.body,
							content: component,
							showOnCreate: true,
							interactive: true,
							trigger: "manual",
							placement: "bottom-start",
						});
					},
					onUpdate(props) {
						selectedIndex = 0;
						currentItems = props.items;
						currentCommand = props.command;
						component.innerHTML = "";

						if (props.items.length === 0) {
							const noResults = document.createElement("div");
							noResults.className = "px-2 py-1.5 text-sm text-muted-foreground";
							noResults.textContent = "No emojis found";
							component.appendChild(noResults);
						} else {
							for (const [index, item] of props.items.entries()) {
								const button = document.createElement("button");
								button.className = `emoji-suggestion-item flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-accent cursor-pointer ${
									index === selectedIndex ? "bg-accent" : ""
								}`;
								button.innerHTML = `<span class="text-lg">${item.emoji}</span><span class="text-foreground">:${item.shortcodes[0]}:</span>`;
								button.addEventListener("click", () => props.command(item));
								component.appendChild(button);
							}
						}

						if (popup?.[0]) {
							popup[0].setProps({
								getReferenceClientRect: props.clientRect as () => DOMRect,
							});
						}
					},
					onKeyDown(props) {
						if (props.event.key === "Escape") {
							popup?.[0]?.hide();
							return true;
						}

						if (props.event.key === "ArrowUp") {
							props.event.preventDefault();
							selectedIndex =
								(selectedIndex - 1 + currentItems.length) % currentItems.length;
							const buttons = component.querySelectorAll(
								".emoji-suggestion-item"
							);
							for (const [i, btn] of buttons.entries()) {
								btn.classList.toggle("bg-accent", i === selectedIndex);
							}
							return true;
						}

						if (props.event.key === "ArrowDown") {
							props.event.preventDefault();
							selectedIndex = (selectedIndex + 1) % currentItems.length;
							const buttons = component.querySelectorAll(
								".emoji-suggestion-item"
							);
							for (const [i, btn] of buttons.entries()) {
								btn.classList.toggle("bg-accent", i === selectedIndex);
							}
							return true;
						}

						if (props.event.key === "Enter" || props.event.key === "Tab") {
							props.event.preventDefault();
							if (currentItems[selectedIndex] && currentCommand) {
								currentCommand(currentItems[selectedIndex]);
							}
							return true;
						}

						return false;
					},
					onExit() {
						popup?.[0]?.destroy();
						component?.remove();
					},
				};
			},
		} as Partial<SuggestionOptions>,
	}),
	getMentionExtension(getMentionItems),
	AutoImagePaste,
	ImageResize,
	HardLineBreak,
	TextStyle,
	Color,
];

export const getTiptapReadOnlyExtensions = () => [
	StarterKit.configure({
		bulletList: {
			keepMarks: true,
			keepAttributes: false,
		},
		orderedList: {
			keepMarks: true,
			keepAttributes: false,
		},
	}),
	Link.configure({
		openOnClick: true,
		autolink: true,
	}),
	Placeholder.configure({
		placeholder: "",
	}),
	TextStyle,
	Color,
	Image.extend({
		inline: true,
		group: "inline",
		addAttributes() {
			return {
				...this.parent?.(),
				width: {
					default: null,
					parseHTML: (element: HTMLElement) => element.getAttribute("width"),
					renderHTML: (attributes: Record<string, any>) => {
						if (!attributes.width) {
							return {};
						}
						return { width: attributes.width };
					},
				},
				height: {
					default: null,
					parseHTML: (element: HTMLElement) => element.getAttribute("height"),
					renderHTML: (attributes: Record<string, any>) => {
						if (!attributes.height) {
							return {};
						}
						return { height: attributes.height };
					},
				},
			};
		},
		addNodeView() {
			return ({ node }: { node: any }) => {
				const container = document.createElement("span");
				container.classList.add("image-container", "inline-block");

				const img = document.createElement("img");
				img.src = node.attrs.src;
				img.alt = node.attrs.alt || "";
				img.classList.add("rounded-lg", "max-w-full", "h-auto");

				if (node.attrs.width) {
					img.setAttribute("width", node.attrs.width);
				}
				if (node.attrs.height) {
					img.setAttribute("height", node.attrs.height);
				}

				img.onerror = () => {
					container.innerHTML = "";
					const link = document.createElement("a");
					link.href = node.attrs.src;
					link.target = "_blank";
					link.rel = "noopener noreferrer";
					link.textContent = node.attrs.src;
					link.classList.add("text-blue-500", "hover:underline", "break-all");
					container.appendChild(link);
				};

				container.appendChild(img);

				return {
					dom: container,
				};
			};
		},
	}).configure({
		inline: false,
		allowBase64: true,
	}),
	Emoji.configure({
		emojis: gitHubEmojis,
		enableEmoticons: true,
	}),
	Mention.configure({
		HTMLAttributes: {
			class: "mention bg-primary/10 text-primary px-1 rounded font-medium",
		},
		renderLabel({ node }) {
			return `@${node.attrs.label || ""}`;
		},
	}),
];

const tiptapBaseStyles = `
	prose prose-sm dark:prose-invert
	w-full outline-none
	
	/* Headings */
	[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-6 [&_h1]:mb-4
	[&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-5 [&_h2]:mb-3
	[&_h3]:text-lg [&_h3]:font-bold [&_h3]:mt-4 [&_h3]:mb-2
	[&_h4]:text-base [&_h4]:font-bold [&_h4]:mt-3 [&_h4]:mb-2
	[&_h5]:text-sm [&_h5]:font-bold [&_h5]:mt-2 [&_h5]:mb-1
	[&_h6]:text-sm [&_h6]:font-semibold [&_h6]:mt-2 [&_h6]:mb-1
	
	/* Paragraphs */
	[&_p]:leading-relaxed [&_p]:text-sm
	[&_p:not(:first-child)]:mt-2 [&_p:not(:last-child)]:mb-2
	
	/* Lists */
	[&_ul]:list-disc [&_ul]:my-2 [&_ul]:pl-6
	[&_ol]:list-decimal [&_ol]:my-2 [&_ol]:pl-6
	[&_li]:my-1
	
	/* Code */
	[&_code]:bg-muted [&_code]:text-foreground [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono
	[&_pre]:bg-muted [&_pre]:text-foreground [&_pre]:p-3 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:my-2
	[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-sm [&_pre_code]:font-mono
	
	/* Links */
	[&_a]:text-primary [&_a]:underline [&_a]:hover:opacity-80
	
	/* Emphasis */
	[&_strong]:font-bold
	[&_em]:italic
	[&_s]:line-through
	
	/* Blockquotes */
	[&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-2
	
	/* Horizontal Rule */
	[&_hr]:my-4 [&_hr]:border-border
	
	/* Images */
	[&_img]:rounded-lg [&_img]:max-w-full [&_img]:h-auto [&_img]:my-2
	
	/* Open Graph Embeds */
	[&_.og-embed-container]:relative [&_.og-embed-container]:inline-block [&_.og-embed-container]:w-full
	[&_.og-embed-card]:block [&_.og-embed-card]:no-underline
`;

export const tiptapEditorStyles = `
	${tiptapBaseStyles}
	scrollbar-track-[transparent] scrollbar-thumb-secondary-foreground/30
	h-full max-h-[200px] overflow-y-auto pr-40
	[&_.ProseMirror:focus]:outline-none [&_.ProseMirror]:outline-none
	
	/* Image editing features */
	[&_img]:cursor-default [&_img]:transition-shadow [&_img]:duration-200 [&_img:hover]:shadow-lg
	[&_img.ProseMirror-selectednode]:outline [&_img.ProseMirror-selectednode]:outline-2 
	[&_img.ProseMirror-selectednode]:outline-primary [&_img.ProseMirror-selectednode]:shadow-xl
	[&_img.ProseMirror-selectednode]:cursor-default
`;

export const tiptapRendererStyles = tiptapBaseStyles;
